import type { RefObject } from 'react';
import { Alert, AlertTitle, Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { borderRadius } from '../../../theme/theme';
import type { ExportRowHighlightColor, ExportRowHighlightMap } from '../lib/rowHighlights';
import {
  formatRemainingExportElementsStatus,
  getRemainingExportElementsHint,
} from '../lib/exportQuota';
import type { summarizePipingSystems } from '../lib/exportSystemStatus';
import type { UseExportStateReturn } from '../hooks/useExportState';
import type { UseExportRunLogReturn } from '../hooks/useExportRunLog';
import type { useExportWorkspaceCommands } from '../hooks/useExportWorkspaceCommands';
import type { usePcfVisualCheck } from '../hooks/usePcfVisualCheck';
import ExportFailuresAlert from './ExportFailuresAlert';
import ExportToolbar from './ExportToolbar';
import ExportWorkspaceActions from './ExportWorkspaceActions';

export const EXPORT_LIVE_SUMMARY_ID = 'export-pcf-live-summary';

const visuallyHiddenSx = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: 1,
} as const;

interface ExportWorkspaceHeaderProps {
  liveSummary: string;
  exportState: UseExportStateReturn;
  exportLog: UseExportRunLogReturn;
  commands: ReturnType<typeof useExportWorkspaceCommands>;
  visualCheck: ReturnType<typeof usePcfVisualCheck>;
  activeBatchSummary: ReturnType<typeof summarizePipingSystems>;
  selected: Set<string>;
  systemNameFilterValue: string;
  onSystemNameFilterChange: (value: string) => void;
  rowHighlightColors: ExportRowHighlightMap;
  showGuideButton: boolean;
  narrow: boolean;
  columnPanelOpen: boolean;
  groupingParameterName: string;
  groupingParameterSource: 'Element' | 'Type';
  quotaLimited: boolean;
  quotaExhausted: boolean;
  quotaRemaining: number | null;
  quotaIsDemo: boolean;
  quotaExhaustedMessage: string;
  exportBusy: boolean;
  exportActionsBlocked: boolean;
  exportBlockedMessage: string;
  headerRef: RefObject<HTMLDivElement | null>;
  refreshButtonRef: RefObject<HTMLButtonElement | null>;
  systemCountRef: RefObject<HTMLElement | null>;
  rowHighlightButtonRef: RefObject<HTMLButtonElement | null>;
  visualCheckButtonRef: RefObject<HTMLButtonElement | null>;
  manageColumnsButtonRef: RefObject<HTMLButtonElement | null>;
  exportSettingsButtonRef: RefObject<HTMLButtonElement | null>;
  selectionSummaryRef: RefObject<HTMLDivElement | null>;
  exportActionsRef: RefObject<HTMLDivElement | null>;
  outputPathRef: RefObject<HTMLDivElement | null>;
  exportButtonRef: RefObject<HTMLButtonElement | null>;
  pickAndExportButtonRef: RefObject<HTMLButtonElement | null>;
  onClearExportResult: () => void;
  onOpenTour: () => void;
  onOpenGuide: () => void;
  onRefreshSystems: () => void;
  onRowHighlightColorChange: (systemName: string, color: ExportRowHighlightColor | null) => void;
  onRowHighlightReset: () => void;
  onToggleColumnPanel: () => void;
  onOpenSettings: () => void;
}

export default function ExportWorkspaceHeader(props: ExportWorkspaceHeaderProps) {
  const theme = useTheme();
  return (
    <>
      <Box id={EXPORT_LIVE_SUMMARY_ID} role="status" aria-live="polite" aria-atomic sx={visuallyHiddenSx}>
        {props.liveSummary}
      </Box>
      {props.exportState.pluginVersionMismatch ? (
        <Alert severity="warning" sx={{ mx: 1, mt: 1, borderRadius: `${borderRadius.md}px` }}>
          The PipeEX plugin in this Revit session is out of date and cannot run the current export.
          Update the plugin and reload Revit to continue.
        </Alert>
      ) : null}
      <ExportFailuresAlert
        entries={props.exportLog.failureNoticeOpen
          ? props.exportLog.log?.failureNoticeEntries ?? []
          : []}
        partialSuccess={Boolean(props.exportLog.log?.hasSuccessfulOutput)}
        onClose={() => {
          props.onClearExportResult();
          props.exportLog.dismissFailureNotice();
        }}
        onOpenLog={props.exportLog.openWindow}
      />
      {props.quotaLimited && !props.quotaExhausted && props.quotaRemaining !== null ? (
        <Box
          role="status"
          data-testid="export-pcf-quota-status"
          sx={{
            mx: 1,
            mt: 1,
            px: 1.25,
            py: 0.5,
            display: 'flex',
            alignItems: 'baseline',
            columnGap: 1,
            rowGap: 0.25,
            flexWrap: 'wrap',
            minWidth: 0,
            borderRadius: `${borderRadius.md}px`,
            border: `1px solid ${alpha(theme.palette.info.main, 0.35)}`,
            backgroundColor: alpha(theme.palette.info.main, 0.06),
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            {formatRemainingExportElementsStatus(props.quotaIsDemo, props.quotaRemaining)}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, minWidth: 0 }}>
            {getRemainingExportElementsHint(props.quotaIsDemo)}
          </Typography>
        </Box>
      ) : null}
      {props.quotaExhausted ? (
        <Alert severity="warning" role="alert" data-testid="export-pcf-quota-exhausted"
          sx={{ mx: 1, mt: 1, borderRadius: `${borderRadius.md}px` }}>
          <AlertTitle sx={{ fontWeight: 600 }}>
            {formatRemainingExportElementsStatus(props.quotaIsDemo, props.quotaRemaining ?? 0)}
          </AlertTitle>
          {props.quotaExhaustedMessage}
        </Alert>
      ) : null}
      <ExportToolbar
        workspaceTitle="Piping systems"
        outputPath={props.exportState.config.outputPath}
        onChangeOutputPath={(outputPath) => props.exportState.setConfig({ outputPath })}
        onBrowseOutputPath={props.commands.handleBrowseOutputPath}
        toolbarRef={props.headerRef}
        systemCountRef={props.systemCountRef}
        outputPathRef={props.outputPathRef}
        systemsLoading={props.exportState.loading}
        systemsStale={props.exportState.systemsStale}
        workspaceActions={(
          <ExportWorkspaceActions
            showGuideButton={props.showGuideButton}
            systems={props.exportState.systems}
            selected={props.selected}
            rowHighlightColors={props.rowHighlightColors}
            loading={props.exportState.loading}
            systemsStale={props.exportState.systemsStale}
            narrow={props.narrow}
            columnPanelOpen={props.columnPanelOpen}
            refreshButtonRef={props.refreshButtonRef}
            rowHighlightButtonRef={props.rowHighlightButtonRef}
            visualCheckButtonRef={props.visualCheckButtonRef}
            manageColumnsButtonRef={props.manageColumnsButtonRef}
            exportSettingsButtonRef={props.exportSettingsButtonRef}
            onOpenTour={props.onOpenTour}
            onOpenGuide={props.onOpenGuide}
            onRefreshSystems={props.onRefreshSystems}
            visualCheckBusy={props.visualCheck.busy}
            groupingParameterName={props.groupingParameterName}
            groupingParameterSource={props.groupingParameterSource}
            onApplyVisualCheck={props.visualCheck.apply}
            onResetVisualCheck={props.visualCheck.reset}
            onRowHighlightColorChange={props.onRowHighlightColorChange}
            onRowHighlightReset={props.onRowHighlightReset}
            onToggleColumnPanel={props.onToggleColumnPanel}
            onOpenSettings={props.onOpenSettings}
          />
        )}
        selectedCount={props.selected.size}
        totalCount={props.exportState.systems.length}
        systemNameFilterValue={props.systemNameFilterValue}
        onSystemNameFilterChange={props.onSystemNameFilterChange}
        exporting={props.exportBusy}
        batchNotValidatedCount={props.activeBatchSummary.notValidated}
        batchWarningCount={props.activeBatchSummary.warnings}
        batchErrorCount={props.activeBatchSummary.errors}
        onSelectInModel={props.commands.handlePickAndExport}
        onExportSelected={props.commands.handleExportSelected}
        onExportAll={props.commands.handleExportAll}
        exportLogEntryCount={props.exportLog.entryCount}
        onOpenExportLog={props.exportLog.openWindow}
        modelSelecting={props.commands.pickExportBusy}
        selectionSummaryRef={props.selectionSummaryRef}
        exportActionsRef={props.exportActionsRef}
        exportButtonRef={props.exportButtonRef}
        pickAndExportButtonRef={props.pickAndExportButtonRef}
        actionsDisabled={props.exportActionsBlocked}
        actionsDisabledReason={props.exportBlockedMessage}
        actionsDisabledTitle={props.quotaExhausted ? 'Export element allowance used' : undefined}
        actionsDisabledNextStep={props.quotaExhausted
          ? 'Contact PipeEX support to extend your allowance or choose a paid license.'
          : undefined}
      />
    </>
  );
}
